import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject, take, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from 'src/app/core/app.state';
import { IRole, IUpdateRole } from 'src/app/features/settings/models/settings.models';
import { FetchAllModules, FetchAllRoles, SetModulesLoader, SetRolesLoader, UpdateRole, UpdateRoleError, UpdateRoleSuccess } from 'src/app/features/settings/store/settings.actions';
import {
  mergeRolePermissionIds,
  RolePermissionModuleView,
  selectAllRoles,
  selectRolePermissionCatalog,
} from 'src/app/features/settings/store/settings-rbac.selectors';
import { SettingsService } from 'src/app/features/settings/services/settings.services';
import { IBreadcrumb } from 'src/app/ui/page-header/page-header.component';
import {
  loadFieldVisibilityCatalog,
} from 'src/app/core/store/field-visibility/field-visibility.actions';
import {
  selectFieldVisibilityCatalog,
  selectFieldVisibilityCatalogLoaded,
} from 'src/app/core/store/field-visibility/field-visibility.selectors';
import {
  FieldVisibilityModule,
  HiddenFieldsByModule,
  IFieldCatalogEntry,
  IFieldVisibilityCatalog,
} from 'src/app/core/store/field-visibility/field-visibility.types';

@Component({
  selector: 'app-role-permissions-detail',
  templateUrl: './role-permissions-detail.component.html',
  styleUrls: ['./role-permissions-detail.component.scss'],
})
export class RolePermissionsDetailComponent implements OnInit, OnDestroy {
  readonly roles$ = this.store.select(selectAllRoles);
  readonly permissionCatalog$ = this.store.select(selectRolePermissionCatalog);
  readonly fieldCatalog$ = this.store.select(selectFieldVisibilityCatalog);
  readonly fieldCatalogLoaded$ = this.store.select(selectFieldVisibilityCatalogLoaded);

  roleId = 0;
  role: IRole | null = null;
  permissionCatalog: RolePermissionModuleView[] = [];
  fieldCatalog: IFieldVisibilityCatalog | null = null;

  activeTab: 'permissions' | 'fieldVisibility' = 'permissions';
  activeFieldModule: FieldVisibilityModule = 'order';
  expandedModuleId: number | null = null;
  selectedPermissionIds = new Set<number>();
  hiddenKeysByModule: Record<FieldVisibilityModule, Set<string>> = {
    order: new Set(),
    product: new Set(),
    company: new Set(),
  };
  hasFieldVisibilityChanges = false;
  hasPermissionsChanged = false;
  isSaving = false;

  readonly breadcrumbs: IBreadcrumb[] = [];
  readonly fieldModules: { key: FieldVisibilityModule; labelKey: string }[] = [
    { key: 'company', labelKey: 'settings.field_visibility_module_customers' },
    { key: 'order', labelKey: 'settings.field_visibility_module_orders' },
    { key: 'product', labelKey: 'settings.field_visibility_module_products' },
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<AppState>,
    private readonly route: ActivatedRoute,
    private readonly translateService: TranslateService,
    private readonly actions$: Actions,
    private readonly settingsService: SettingsService,
  ) {}

  ngOnInit(): void {
    this.roleId = Number(this.route.snapshot.paramMap.get('id'));
    this.translateService
      .stream('settings.roles_permissions')
      .pipe(takeUntil(this.destroy$))
      .subscribe((label) => {
        this.breadcrumbs.splice(0, this.breadcrumbs.length, {
          label,
          link: '/settings/roles',
        });
      });

    this.store.dispatch(SetRolesLoader({ isLoading: true }));
    this.store.dispatch(SetModulesLoader({ isLoading: true }));
    this.store.dispatch(FetchAllRoles());
    this.store.dispatch(FetchAllModules());

    this.roles$
      .pipe(takeUntil(this.destroy$))
      .subscribe((roles) => this.syncRole(roles));

    this.permissionCatalog$
      .pipe(takeUntil(this.destroy$))
      .subscribe((permissionCatalog) => {
        this.permissionCatalog = permissionCatalog ?? [];
      });

    this.fieldCatalog$
      .pipe(takeUntil(this.destroy$))
      .subscribe((catalog) => {
        this.fieldCatalog = catalog;
      });

    this.fieldCatalogLoaded$
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(loadFieldVisibilityCatalog());
        }
      });

    this.loadRoleHiddenFields();

    this.actions$
      .pipe(ofType(UpdateRoleSuccess), takeUntil(this.destroy$))
      .subscribe(() => {
        this.isSaving = false;
        this.hasPermissionsChanged = false;
        this.hasFieldVisibilityChanges = false;
      });

    this.actions$
      .pipe(ofType(UpdateRoleError), takeUntil(this.destroy$))
      .subscribe(() => {
        this.isSaving = false;
        // Resync local hidden-fields state from the server so the UI matches
        // what's persisted after the backend rejected our write.
        this.loadRoleHiddenFields();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get roleTypeTag(): string {
    return this.translateService.instant(
      this.role?.is_protected ? 'settings.system_role' : 'settings.custom_role'
    );
  }

  get roleTypeTagType(): 'secondary' | 'info' {
    return this.role?.is_protected ? 'secondary' : 'info';
  }

  toggleModuleExpanded(module: RolePermissionModuleView): void {
    if (!this.isModuleAccessEnabled(module)) return;
    this.expandedModuleId = this.expandedModuleId === module.id ? null : module.id;
  }

  isModuleExpanded(module: RolePermissionModuleView): boolean {
    return this.expandedModuleId === module.id;
  }

  getSelectedModulePermissionsCount(module: RolePermissionModuleView): number {
    const effective = this.effectiveSelectedPermissionIds();
    return module.permissionIds.filter((permissionId) => effective.has(permissionId)).length;
  }

  isModuleAccessEnabled(module: RolePermissionModuleView): boolean {
    if (!module.hasAccessToggle) {
      return true;
    }

    if (!module.accessPermissionId) {
      return this.getSelectedModulePermissionsCount(module) > 0;
    }

    return this.effectiveSelectedPermissionIds().has(module.accessPermissionId);
  }

  shouldShowModuleActionsSummary(module: RolePermissionModuleView): boolean {
    return module.actionPermissions.length > 0 && this.isModuleAccessEnabled(module);
  }

  getSelectedModuleActionsCount(module: RolePermissionModuleView): number {
    const effective = this.effectiveSelectedPermissionIds();
    return module.actionPermissions.filter((action) => effective.has(action.permissionId)).length;
  }

  onModuleAccessClick(event: Event): void {
    event.stopPropagation();
  }

  onModuleAccessChanged(module: RolePermissionModuleView, event: Event): void {
    event.stopPropagation();
    if (this.role?.is_protected || !module.hasAccessToggle) return;

    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    this.setModuleAccess(module, target.checked);
  }

  isSelected(permissionId: number): boolean {
    return this.effectiveSelectedPermissionIds().has(permissionId);
  }

  togglePermission(module: RolePermissionModuleView, permissionId: number): void {
    if (this.role?.is_protected) return;

    if (this.selectedPermissionIds.has(permissionId)) {
      this.selectedPermissionIds.delete(permissionId);
      this.hasPermissionsChanged = true;
      return;
    }

    if (module.accessPermissionId) {
      this.selectedPermissionIds.add(module.accessPermissionId);
    }
    this.selectedPermissionIds.add(permissionId);
    this.hasPermissionsChanged = true;
  }

  isDataScopeOwn(module: RolePermissionModuleView): boolean {
    return module.dataScopePermissionId !== null &&
      this.effectiveSelectedPermissionIds().has(module.dataScopePermissionId);
  }

  setDataScopeAll(module: RolePermissionModuleView): void {
    if (!module.dataScopePermissionId || this.role?.is_protected) return;
    this.selectedPermissionIds.delete(module.dataScopePermissionId);
    this.hasPermissionsChanged = true;
  }

  setDataScopeOwn(module: RolePermissionModuleView): void {
    if (!module.dataScopePermissionId || this.role?.is_protected) return;
    this.selectedPermissionIds.add(module.dataScopePermissionId);
    this.hasPermissionsChanged = true;
  }

  getModuleLabel(module: RolePermissionModuleView): string {
    const translationKey = `settings.role_modules.${module.name}`;
    const translated = this.translateService.instant(translationKey);
    return translated !== translationKey ? translated : module.label;
  }

  getActionLabel(action: string): string {
    const normalizedAction = (action ?? '').toLowerCase();
    const translationKey = `user.permissions.${normalizedAction}`;
    const translated = this.translateService.instant(translationKey);
    return translated !== translationKey ? translated : action;
  }

  // ===== Field Visibility =====

  setActiveFieldModule(module: FieldVisibilityModule): void {
    this.activeFieldModule = module;
  }

  getSystemFieldsFor(module: FieldVisibilityModule): IFieldCatalogEntry[] {
    return this.fieldCatalog?.[module]?.system ?? [];
  }

  getFieldLabel(entry: IFieldCatalogEntry): string {
    const translationKey = `settings.field_labels.${entry.key}`;
    const translated = this.translateService.instant(translationKey);
    return translated !== translationKey ? translated : entry.label;
  }

  getFieldTypeLabel(type: string): string {
    const normalized = (type ?? '').trim().toLowerCase().replace(/\s+/g, '_');
    if (!normalized) return type;
    const translationKey = `settings.field_visibility_types.${normalized}`;
    const translated = this.translateService.instant(translationKey);
    return translated !== translationKey ? translated : type;
  }

  isFieldHidden(module: FieldVisibilityModule, key: string): boolean {
    return this.hiddenKeysByModule[module]?.has(key) ?? false;
  }

  toggleFieldHidden(module: FieldVisibilityModule, entry: IFieldCatalogEntry): void {
    if (this.role?.is_protected || entry.is_locked) return;
    const set = this.hiddenKeysByModule[module];
    if (set.has(entry.key)) {
      set.delete(entry.key);
    } else {
      set.add(entry.key);
    }
    this.hasFieldVisibilityChanges = true;
  }

  setFieldVisibility(module: FieldVisibilityModule, entry: IFieldCatalogEntry, hidden: boolean): void {
    if (this.role?.is_protected || (entry.is_locked && hidden)) return;
    const set = this.hiddenKeysByModule[module];
    const wasHidden = set.has(entry.key);
    if (hidden && !wasHidden) {
      set.add(entry.key);
      this.hasFieldVisibilityChanges = true;
    } else if (!hidden && wasHidden) {
      set.delete(entry.key);
      this.hasFieldVisibilityChanges = true;
    }
  }

  countVisible(module: FieldVisibilityModule): number {
    const all = this.getSystemFieldsFor(module);
    const hidden = this.hiddenKeysByModule[module];
    return all.filter((entry) => !hidden.has(entry.key)).length;
  }

  countHidden(module: FieldVisibilityModule): number {
    const all = this.getSystemFieldsFor(module);
    const hidden = this.hiddenKeysByModule[module];
    return all.filter((entry) => hidden.has(entry.key)).length;
  }

  countTotal(module: FieldVisibilityModule): number {
    return this.getSystemFieldsFor(module).length;
  }

  setAllVisible(): void {
    if (this.role?.is_protected) return;
    this.hiddenKeysByModule[this.activeFieldModule] = new Set();
    this.hasFieldVisibilityChanges = true;
  }

  setAllHidden(): void {
    if (this.role?.is_protected) return;
    const all = this.getSystemFieldsFor(this.activeFieldModule);
    const next = new Set<string>();
    for (const entry of all) {
      if (!entry.is_locked) next.add(entry.key);
    }
    this.hiddenKeysByModule[this.activeFieldModule] = next;
    this.hasFieldVisibilityChanges = true;
  }

  getTypeBadgeClass(type: string): string {
    const normalized = (type ?? '').toUpperCase().replace(/\s+/g, '_').toLowerCase();
    return `role-permissions-detail__type-badge role-permissions-detail__type-badge--${normalized || 'default'}`;
  }

  saveChanges(): void {
    if (!this.role || this.role.is_protected || this.isSaving) return;
    if (!this.hasPermissionsChanged && !this.hasFieldVisibilityChanges) return;

    const payload: IUpdateRole = { name: this.role.name };

    if (this.hasPermissionsChanged) {
      payload.permission_ids = Array.from(this.selectedPermissionIds);
    }

    if (this.hasFieldVisibilityChanges) {
      payload.hidden_field_ids = {
        order: Array.from(this.hiddenKeysByModule.order).sort(),
        product: Array.from(this.hiddenKeysByModule.product).sort(),
        company: Array.from(this.hiddenKeysByModule.company).sort(),
      };
    }

    this.isSaving = true;
    this.store.dispatch(UpdateRole({ id: this.role.id, role: payload }));
  }

  trackByModuleId(_: number, module: RolePermissionModuleView): number {
    return module.id;
  }

  trackByPermissionAction(_: number, action: { permissionId: number }): number {
    return action.permissionId;
  }

  trackByFieldEntryKey(_: number, entry: IFieldCatalogEntry): string {
    return entry.key;
  }

  private syncRole(roles: IRole[]): void {
    const selectedRole = roles.find((role) => role.id === this.roleId) ?? null;
    if (!selectedRole) return;

    const previousId = this.role?.id;
    this.role = selectedRole;
    this.selectedPermissionIds = mergeRolePermissionIds(selectedRole);
    this.hasPermissionsChanged = false;

    // Refetch hidden fields when the role id changes (typical first sync).
    if (previousId !== selectedRole.id) {
      this.loadRoleHiddenFields();
    }
  }

  private loadRoleHiddenFields(): void {
    if (!this.roleId) return;
    this.settingsService.getRoleHiddenFields(this.roleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const data = (response?.object ?? response?.data ?? {}) as HiddenFieldsByModule;
          this.hiddenKeysByModule = {
            order: new Set(data.order ?? []),
            product: new Set(data.product ?? []),
            company: new Set(data.company ?? []),
          };
          this.hasFieldVisibilityChanges = false;
        },
        error: () => {
          // Leave defaults (empty sets). Errors are non-fatal for the UI; PATCH errors are surfaced on save.
        },
      });
  }

  private effectiveSelectedPermissionIds(): Set<number> {
    if (!this.role?.is_protected) {
      return this.selectedPermissionIds;
    }

    const all = new Set<number>(this.selectedPermissionIds);
    for (const module of this.permissionCatalog) {
      for (const permissionId of module.permissionIds) {
        all.add(permissionId);
      }
    }
    return all;
  }

  private setModuleAccess(module: RolePermissionModuleView, enabled: boolean): void {
    if (!module.hasAccessToggle) {
      return;
    }

    if (enabled) {
      module.permissionIds.forEach((permissionId) => this.selectedPermissionIds.add(permissionId));
      this.hasPermissionsChanged = true;
      return;
    }

    module.permissionIds.forEach((permissionId) => this.selectedPermissionIds.delete(permissionId));
    this.hasPermissionsChanged = true;
  }
}
